import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import api from '../lib/api';
import { toast } from 'sonner';

const OCN_ERROR_MESSAGES = {
  ocn_not_enabled_for_org: 'OCN är inte aktiverat för denna organisation.',
  ocn_only_employees: 'OCN-behörighet kan endast tilldelas till handläggare.',
  ocn_no_self_grant: 'Du kan inte tilldela OCN-behörighet till dig själv.',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });
};

const extractApiError = (err) => {
  const rc = err.response?.data?.detail?.reason_code;
  return OCN_ERROR_MESSAGES[rc] || 'Något gick fel. Försök igen.';
};

const today = () => new Date().toISOString().slice(0, 10);

const OCNPermissionDialog = ({ user, open, onClose, onUpdated }) => {
  const [ocnData, setOcnData]           = useState(null);
  const [loadError, setLoadError]       = useState('');
  const [loading, setLoading]           = useState(false);
  const [certRef, setCertRef]           = useState('');
  const [certDate, setCertDate]         = useState('');
  const [saving, setSaving]             = useState(false);
  const [submitError, setSubmitError]   = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    setLoadError('');
    setSubmitError('');
    setConfirmRevoke(false);
    api.get(`/users/${user.id}/ocn-access`)
      .then(r => {
        setOcnData(r.data);
        setCertRef(r.data.ocn_access_certification_ref || '');
        setCertDate(r.data.ocn_access_certification_date || '');
      })
      .catch(() => setLoadError('Kunde inte hämta OCN-status.'))
      .finally(() => setLoading(false));
  }, [open, user]);

  const handleClose = () => {
    setCertRef('');
    setCertDate('');
    setSubmitError('');
    setConfirmRevoke(false);
    setOcnData(null);
    onClose();
  };

  const handleGrant = async (e) => {
    e.preventDefault();
    if (!certRef.trim()) { setSubmitError('Certifieringsreferens krävs.'); return; }
    if (!certDate)        { setSubmitError('Certifieringsdatum krävs.'); return; }
    if (certDate > today()) { setSubmitError('Certifieringsdatum får inte vara i framtiden.'); return; }
    setSaving(true);
    setSubmitError('');
    try {
      const r = await api.post(`/users/${user.id}/ocn-access`, {
        certification_ref: certRef.trim(),
        certification_date: certDate,
      });
      setOcnData(r.data);
      if (onUpdated) onUpdated(r.data);
      toast.success(ocnData?.ocn_access ? 'Certifiering uppdaterad.' : 'OCN-behörighet tilldelad.');
    } catch (err) {
      setSubmitError(extractApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    setSaving(true);
    setSubmitError('');
    try {
      const r = await api.delete(`/users/${user.id}/ocn-access`);
      setOcnData(r.data);
      if (onUpdated) onUpdated(r.data);
      setConfirmRevoke(false);
      toast.success('OCN-behörighet återkallad.');
    } catch (err) {
      setSubmitError(extractApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const hasAccess = ocnData?.ocn_access === true;

  return (
    <Dialog open={open} onOpenChange={isOpen => { if (!isOpen) handleClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading font-bold">
            OCN-behörighet — {user?.name}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <p className="text-sm text-muted-foreground py-4">Laddar...</p>
        )}

        {loadError && (
          <p data-testid="ocn-load-error" className="text-sm text-destructive py-4">{loadError}</p>
        )}

        {!loading && !loadError && ocnData && (
          <div className="space-y-5">

            {/* Current status */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-muted-foreground">Nuvarande status:</span>
              {hasAccess ? (
                <span data-testid="ocn-badge-active" className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
                  Aktiv
                </span>
              ) : (
                <span data-testid="ocn-badge-inactive" className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600">
                  Ej tilldelad
                </span>
              )}
            </div>

            {/* Grant history */}
            {hasAccess && (
              <div data-testid="ocn-grant-history" className="rounded-lg bg-muted p-4 space-y-1.5 text-sm">
                {ocnData.ocn_access_granted_at && (
                  <p><span className="text-muted-foreground">Tilldelad:</span> {formatDate(ocnData.ocn_access_granted_at)}</p>
                )}
                {ocnData.ocn_access_granted_by_user_id && (
                  <p><span className="text-muted-foreground">Av (användar-ID):</span> {ocnData.ocn_access_granted_by_user_id}</p>
                )}
                {ocnData.ocn_access_certification_ref && (
                  <p><span className="text-muted-foreground">Certifiering:</span> {ocnData.ocn_access_certification_ref}</p>
                )}
                {ocnData.ocn_access_certification_date && (
                  <p><span className="text-muted-foreground">Certifieringsdatum:</span> {ocnData.ocn_access_certification_date}</p>
                )}
              </div>
            )}

            {/* Grant / update form */}
            <form onSubmit={handleGrant} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="ocn-cert-ref">Certifieringsreferens *</Label>
                <Input
                  id="ocn-cert-ref"
                  data-testid="ocn-cert-ref"
                  value={certRef}
                  onChange={e => setCertRef(e.target.value)}
                  maxLength={200}
                  placeholder="T.ex. OCN Level 2 — Stockholm Universitet 2024-09-15"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ocn-cert-date">Certifieringsdatum *</Label>
                <Input
                  id="ocn-cert-date"
                  data-testid="ocn-cert-date"
                  type="date"
                  value={certDate}
                  onChange={e => setCertDate(e.target.value)}
                  max={today()}
                />
              </div>

              {submitError && (
                <p data-testid="ocn-submit-error" className="text-sm text-destructive">{submitError}</p>
              )}

              <Button
                type="submit"
                data-testid="ocn-grant-btn"
                disabled={saving}
                className="w-full rounded-full font-bold"
              >
                {saving
                  ? 'Sparar...'
                  : hasAccess
                    ? 'Uppdatera certifiering'
                    : 'Tilldela OCN-behörighet'}
              </Button>
            </form>

            {/* Revoke section */}
            {hasAccess && (
              <div className="border-t pt-4">
                {!confirmRevoke ? (
                  <Button
                    variant="outline"
                    data-testid="ocn-revoke-btn"
                    onClick={() => setConfirmRevoke(true)}
                    disabled={saving}
                    className="w-full rounded-full font-bold text-red-600 hover:bg-red-50 border-red-200"
                  >
                    Återkalla OCN-behörighet
                  </Button>
                ) : (
                  <div data-testid="ocn-revoke-confirm" className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Är du säker? Användaren förlorar omedelbart åtkomst till OCN.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        data-testid="ocn-confirm-revoke"
                        variant="destructive"
                        onClick={handleRevoke}
                        disabled={saving}
                        className="flex-1 rounded-full font-bold text-sm"
                      >
                        {saving ? 'Återkallar...' : 'Ja, återkalla'}
                      </Button>
                      <Button
                        variant="outline"
                        data-testid="ocn-cancel-revoke"
                        onClick={() => setConfirmRevoke(false)}
                        disabled={saving}
                        className="flex-1 rounded-full text-sm"
                      >
                        Avbryt
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OCNPermissionDialog;
